import { Injectable, inject } from '@angular/core';
import { Observable, finalize, map, shareReplay, switchMap } from 'rxjs';

import { AuthService as AuthApiService } from '../../../../generated/api/auth/auth.service';
import { GetMe200 } from '../../../../generated/model/getMe200';
import { TestLoginAvailability200 } from '../../../../generated/model/testLoginAvailability200';
import { AuthStore } from '@/core/auth/stores/auth.store';
import { isLoginAttempt } from '@/core/auth/login-attempt-context';
import { isSessionRefresh } from '@/core/auth/session-refresh-context';
import { LessonAccessService } from '@/shared/content/lesson-access/lesson-access.service';
import { toVerifiedLessonCode } from './lesson-code-mappers/lesson-code-mappers';
import { VerifiedLessonCodeView } from './lesson-code-mappers/lesson-code-mappers.types';

@Injectable({ providedIn: 'root' })
export class AuthFeatureService {
  private readonly api = inject(AuthApiService);
  private readonly store = inject(AuthStore);
  private readonly lessonAccess = inject(LessonAccessService);

  login(email: string, password: string): Observable<void> {
    return this.api
      .testLogin({ email, password }, { context: isLoginAttempt() })
      .pipe(switchMap(() => this.refreshSession()));
  }

  availability(): Observable<TestLoginAvailability200> {
    return this.api.testLoginAvailability();
  }

  ssoLogin(token: string): Observable<void> {
    return this.api
      .ssoLogin({ accessToken: token }, { context: isLoginAttempt() })
      .pipe(switchMap(() => this.refreshSession()));
  }

  googleLogin(idToken: string): Observable<void> {
    return this.api
      .ssoGoogleLogin({ idToken }, { context: isLoginAttempt() })
      .pipe(switchMap(() => this.refreshSession()));
  }

  verifyLessonCode(code: string): Observable<VerifiedLessonCodeView> {
    return this.api.lookupSessionCode(code).pipe(map(toVerifiedLessonCode));
  }

  loginByCode(code: string, userId: number): Observable<void> {
    return this.api
      .sessionCodeLogin({ code, userId }, { context: isLoginAttempt() })
      .pipe(switchMap(() => this.refreshSession()));
  }

  private inflightRefresh: Observable<void> | null = null;

  // Route guards on the same route run concurrently, so a hard reload fires
  // parallel refreshes; sharing one in-flight call avoids duplicate
  // /sso/refresh + /auth/me round trips.
  refreshSession(): Observable<void> {
    this.inflightRefresh ??= this.api.refreshSession({ context: isSessionRefresh() }).pipe(
      switchMap((response) => {
        this.store.setSession(response.accessToken);
        return this.api.getMe<GetMe200>({ context: isSessionRefresh() });
      }),
      map((me) =>
        this.store.setUserProfile({
          id: me.id,
          name: me.name,
          email: me.email,
          schoolId: me.schoolId,
          schoolGroupName: me.schoolGroupName,
          classGroupId: me.classGroupId,
          gradeName: me.gradeName,
          serieSlug: me.serieSlug,
          seenHints: me.seenHints,
        }),
      ),
      finalize(() => (this.inflightRefresh = null)),
      shareReplay(1),
    );
    return this.inflightRefresh;
  }

  logout(): Observable<void> {
    return this.api.logout().pipe(map(() => this.endSession()));
  }

  localLogout(): void {
    this.endSession();
  }

  private endSession(): void {
    this.store.clear();
    this.lessonAccess.clearRedirect();
  }
}
